import React, { useState, useEffect, useRef }  from 'react';
import { PostRequest, GetRequest } from 'components/common/index.js';
import { getAvailableThemes, getThemeDefaultHighlightColor } from 'components/quote_builder_widget/themes';
import WidgetPreview from 'components/quote_builder_widget/WidgetPreview';
import CssEditorModal from 'components/quote_builder_widget/CssEditorModal';
import CartonMaterialsSection from 'components/quote_builder_widget/CartonMaterialsSection';
import CorrugatedCardboardMaterialsSection from 'components/quote_builder_widget/CorrugatedCardboardMaterialsSection';

const SAMPLE_TEMPLATE_IDENTIFIER = 'becf-12017';

// Themes that support custom CSS
const THEMES_WITH_CUSTOM_CSS = ['dark', 'default', 'minimal', 'compact'];

// Available font families for widget customization
const FONT_FAMILIES = [
  { value: '', label: 'Default (System)' },
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Helvetica, Arial, sans-serif', label: 'Helvetica' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Verdana, sans-serif', label: 'Verdana' },
  { value: 'Tahoma, sans-serif', label: 'Tahoma' },
  { value: '"Trebuchet MS", sans-serif', label: 'Trebuchet MS' },
  { value: '"Times New Roman", serif', label: 'Times New Roman' },
  { value: '"Courier New", monospace', label: 'Courier New' },
  { value: 'system-ui, sans-serif', label: 'System UI' },
];

// Available unit options for widget configuration
const UNIT_OPTIONS = [
  { value: 'both', label: 'quote_builder_widget.unit_option_both' },
  { value: 'mm', label: 'quote_builder_widget.unit_option_mm' },
  { value: 'in', label: 'quote_builder_widget.unit_option_in' },
];

// Map user's default unit to widget unit option
const getDefaultUnitOption = (defaultUnit) => {
  if (defaultUnit === 'in') {
    return 'in';
  }
  return 'mm';
};

const FormNew = ( { identifier, setMode, defaultUnit = 'mm' } ) => {
  const domainInputRef = useRef();
  const initialFetchDone = useRef(false);
  const [domains, setDomains] = useState( [] );
  const [selectedTheme, setSelectedTheme] = useState('default');
  const [highlightColor, setHighlightColor] = useState(getThemeDefaultHighlightColor('default'));
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const previousThemeRef = useRef('default');
  const [fontFamily, setFontFamily] = useState('');
  // For new widgets, default to user's preferred unit; for existing widgets, will be loaded from API
  const [unitOption, setUnitOption] = useState(identifier ? 'both' : getDefaultUnitOption(defaultUnit));
  const [customCss, setCustomCss] = useState('');
  const [isCssEditorOpen, setIsCssEditorOpen] = useState(false);
  const [showCartonMaterials, setShowCartonMaterials] = useState(true);
  const [showCorrugatedCardboardMaterials, setShowCorrugatedCardboardMaterials] = useState(true);
  const availableThemes = getAvailableThemes();

  const [addDomainFormVisible, setAddDomainFormVisible] = useState(false)
  const [addDomainFormHighlighed, setAddDomainFormHighlighed] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [successMessage, setSuccessMessage] = useState("")
  const [newDomain, setNewDomain] = useState("")
  const [savedIdentifier, setSavedIdentifier] = useState(identifier)
  const [previewKey, setPreviewKey] = useState(Date.now())

  const supportsCustomCss = THEMES_WITH_CUSTOM_CSS.includes(selectedTheme);

  // Send style updates to widget preview iframe for live preview
  const sendStyleUpdate = (type, data) => {
    const iframes = document.querySelectorAll('iframe');
    iframes.forEach((iframe) => {
      try {
        iframe.contentWindow.postMessage({ type, ...data }, '*');
      } catch (error) {
        // Ignore cross-origin errors
      }
    });
  };

  useEffect(() => {
    if (supportsCustomCss && savedIdentifier) {
      sendStyleUpdate('diecuttemplates.update_background_color', { color: backgroundColor });
    }
  }, [backgroundColor, supportsCustomCss, savedIdentifier]);

  useEffect(() => {
    if (supportsCustomCss && savedIdentifier) {
      sendStyleUpdate('diecuttemplates.update_font_family', { fontFamily: fontFamily });
    }
  }, [fontFamily, supportsCustomCss, savedIdentifier]);

  useEffect(() => {
    if (supportsCustomCss && savedIdentifier) {
      sendStyleUpdate('diecuttemplates.update_highlight_color', { color: highlightColor });
    }
  }, [highlightColor, supportsCustomCss, savedIdentifier]);

  // Update highlight color to new theme's default when theme changes
  // (only if user was using the old theme's default)
  useEffect(() => {
    if (!initialFetchDone.current) {
      return;
    }
    const previousTheme = previousThemeRef.current;
    const previousDefault = getThemeDefaultHighlightColor(previousTheme);
    const newDefault = getThemeDefaultHighlightColor(selectedTheme);

    // If user was using the previous theme's default, update to new theme's default
    if (highlightColor === previousDefault && previousDefault !== newDefault) {
      setHighlightColor(newDefault);
    }
    previousThemeRef.current = selectedTheme;
  }, [selectedTheme]);

  const fetchWidget = () => {
    if(identifier == undefined) { return }

    GetRequest({
      url: `/quote-builder/user-quote-builder-widgets/${identifier}` ,
      callback: (data) => {
        const theme = data.properties.theme || 'default';
        setDomains( () => data.domains );
        setSelectedTheme( () => theme );
        setHighlightColor( () => data.properties.highlight_color || getThemeDefaultHighlightColor(theme) );
        setBackgroundColor( () => data.properties.background_color || '#ffffff' );
        setFontFamily( () => data.properties.font_family || '' );
        setUnitOption( () => data.properties.unit_option || 'both' );
        setCustomCss( () => data.custom_css || '' );
        setShowCartonMaterials( () => data.properties.show_carton_materials !== false );
        setShowCorrugatedCardboardMaterials( () => data.properties.show_corrugated_cardboard_materials !== false );
        previousThemeRef.current = theme;
        // Delay setting the flag to allow the domains useEffect to skip the initial render
        setTimeout(() => {
          initialFetchDone.current = true;
        }, 100);
      }
    })
  }

  const addToDomains = () => {
    if(newDomain.split('.').length == 1) { return }

    setDomains( (prev) => prev.concat( [newDomain] ).filter(onlyUnique)  )
    setAddDomainFormHighlighed( () => false )
    setAddDomainFormVisible( () => false );
    setNewDomain( () => '' );
  }

  const removeFromDomains = (domain) => {
    setDomains( (prev) => prev.filter( (d) => d != domain ))
  }

  const domainWithHttp = (domain) => `https://${domain}`

  const domainsList = () => {
    return (<div>
      {domains.map( (domain) => {
        return <div key={domain}>
          <span className="domain-name"> {domainWithHttp(domain)} </span>
          <button
            type="button"
            className="btn btn-sm"
            style={{ color: '#dc3545', padding: '2px 8px' }}
            onClick={() => removeFromDomains(domain)}
          >
            <i className="fa fa-trash-o" aria-hidden="true"></i>
            {t('quote_builder_widget.remove') || 'Remove'}
          </button>
        </div>
      } )  }
    </div>)
  }

  const addDomainForm = () => {
    return (
      <div className="form-inline">
        <div className="form-group">
          <div className="input-group">
            <div className="input-group-addon"> https://
            </div>
            <input type="text" className="form-control" size="35" ref={domainInputRef} value={newDomain} placeholder="www.example.com" onChange={ (e) => setNewDomain( () => e.target.value ) }/>
          </div>
        </div>

        <button type="button" className="btn simple-btn" onClick={addToDomains}> {t('quote_builder_widget.save')} </button>
        <button className="btn" type="button" onClick={ () => setAddDomainFormVisible( () => false ) }> {t('quote_builder_widget.cancel')} </button>
    </div>)
  }

  const handleCancel = (e) => {
    e.preventDefault();
    setMode();
  }

  // Reusable save function that can be called with current or overridden values
  const saveWidget = (overrides = {}) => {
    const domainsToSave = overrides.domains !== undefined ? overrides.domains : domains;

    const saveData = {
      identifier: savedIdentifier,
      domains: domainsToSave,
      theme: selectedTheme,
    };

    // Include styling options for themes that support it
    if (supportsCustomCss) {
      saveData.highlight_color = highlightColor;
      saveData.background_color = backgroundColor;
      saveData.font_family = fontFamily;
      saveData.custom_css = overrides.customCss !== undefined ? overrides.customCss : customCss;
    }

    // Always include unit option
    saveData.unit_option = unitOption;

    // Always include show_carton_materials
    saveData.show_carton_materials = showCartonMaterials;

    // Always include show_corrugated_cardboard_materials
    saveData.show_corrugated_cardboard_materials = showCorrugatedCardboardMaterials;

    PostRequest({
      url: '/quote-builder/user-quote-builder-widgets',
      data: saveData,
      callback: (data) => {
        setSuccessMessage(t('quote_builder_widget.saved_successfully'));
        setPreviewKey(Date.now());
        if (!savedIdentifier) {
          setSavedIdentifier(data.identifier);
          window.history.replaceState({}, '', `/quote-builder/widgets/${data.identifier}/edit`);
        }
      },
      400: (data) => {
        setErrorMessage(data.error);
      }
    });
  };

  // Auto-save when domains change (after initial fetch)
  useEffect(() => {
    if (!initialFetchDone.current) {
      return;
    }
    if (domains.length === 0) {
      return;
    }
    saveWidget();
  }, [domains]);

  // Auto-save when showCartonMaterials changes (after initial fetch)
  useEffect(() => {
    if (!initialFetchDone.current) {
      return;
    }
    saveWidget();
  }, [showCartonMaterials]);

  // Auto-save when showCorrugatedCardboardMaterials changes (after initial fetch)
  useEffect(() => {
    if (!initialFetchDone.current) {
      return;
    }
    saveWidget();
  }, [showCorrugatedCardboardMaterials]);

  const handleSave = (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if(domains.length == 0) {
      setAddDomainFormVisible( () => true )
      setAddDomainFormHighlighed( () => true )
      $(domainInputRef.current).focus();
      return;
    }

    saveWidget();
  }

  // useEffect
  useEffect(fetchWidget, [identifier])
  // useEffect

  return (<div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '30px', alignItems: 'flex-start' }}>
      {/* Settings Column */}
      <div style={{ flex: '1 1 500px', minWidth: '300px' }}>
        <table className="table table-striped table-bordered-outside quote_builder_table">
          <tbody>
            <tr>
              <th colSpan="2"> { identifier ? t('quote_builder_widget.update_title') : t('quote_builder_widget.new_title') } </th>
            </tr>

            <tr>
              <td colSpan="2">
                <div style={{ marginBottom: '10px' }}>
                  <strong>{t('quote_builder_widget.domains')}</strong>
                  <br />
                  <small style={{ color: '#666' }}>{t('quote_builder_widget.domains_info')}</small>
                </div>
                <div style={ addDomainFormHighlighed ? { padding: "20px", border: "1px solid #ff0000" } : {}}>
                  {domainsList()}
                  <button className="btn simple-btn" onClick={ (e) => { e.preventDefault(); setAddDomainFormVisible( () => true ) } } >
                    <i className="fa fa-plus-square-o" aria-hidden="true"></i> {t('quote_builder_widget.add_domain')}
                  </button>
                  {addDomainFormVisible && <> <br /> <br /> {addDomainForm()} </>}
                </div>
              </td>
            </tr>

            <tr>
              <td> {t('quote_builder_widget.theme')} <br /> <small> {t('quote_builder_widget.theme_info')} </small> </td>
              <td>
                <select
                  className="form-control"
                  value={selectedTheme}
                  onChange={(e) => setSelectedTheme(() => e.target.value)}
                  style={{ width: '200px' }}
                >
                  {availableThemes.map((themeName) => (
                    <option key={themeName} value={themeName}>
                      {themeName.charAt(0).toUpperCase() + themeName.slice(1)}
                    </option>
                  ))}
                </select>
              </td>
            </tr>

            <tr>
              <td> {t('quote_builder_widget.unit_option')} <br /> <small> {t('quote_builder_widget.unit_option_info')} </small> </td>
              <td>
                <select
                  className="form-control"
                  value={unitOption}
                  onChange={(e) => setUnitOption(() => e.target.value)}
                  style={{ width: '200px' }}
                >
                  {UNIT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {t(option.label)}
                    </option>
                  ))}
                </select>
              </td>
            </tr>

            {supportsCustomCss && (
              <tr>
                <td>
                  {t('quote_builder_widget.highlight_color')} <br />
                  <small>{t('quote_builder_widget.highlight_color_info')}</small>
                </td>
                <td>
                  <input
                    type="color"
                    value={highlightColor}
                    onChange={(e) => setHighlightColor(() => e.target.value)}
                  />
                </td>
              </tr>
            )}

            {supportsCustomCss && (
              <tr>
                <td>
                  {t('quote_builder_widget.background_color')} <br />
                  <small>{t('quote_builder_widget.background_color_info')}</small>
                </td>
                <td>
                  <input
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(() => e.target.value)}
                  />
                </td>
              </tr>
            )}

            {supportsCustomCss && (
              <tr>
                <td>
                  {t('quote_builder_widget.font_family')} <br />
                  <small>{t('quote_builder_widget.font_family_info')}</small>
                </td>
                <td>
                  <select
                    className="form-control"
                    value={fontFamily}
                    onChange={(e) => setFontFamily(() => e.target.value)}
                    style={{ width: '200px' }}
                  >
                    {FONT_FAMILIES.map((font) => (
                      <option key={font.value} value={font.value} style={{ fontFamily: font.value || 'inherit' }}>
                        {font.label}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            )}

            {supportsCustomCss && (
              <tr>
                <td>
                  {t('quote_builder_widget.custom_css')} <br />
                  <small>{t('quote_builder_widget.custom_css_info')}</small>
                </td>
                <td>
                  <button
                    type="button"
                    className="btn simple-btn"
                    onClick={() => setIsCssEditorOpen(true)}
                  >
                    <i className="fa fa-code" aria-hidden="true"></i>{' '}
                    {customCss ? t('quote_builder_widget.edit_css') : t('quote_builder_widget.add_custom_css')}
                  </button>
                  {customCss && (
                    <span style={{ marginLeft: '10px', color: '#28a745' }}>
                      <i className="fa fa-check-circle" aria-hidden="true"></i>{' '}
                      {t('quote_builder_widget.custom_css_applied')}
                    </span>
                  )}
                </td>
              </tr>
            )}

            <tr>
              <td>
                {t('quote_builder_widget.show_carton_materials') || 'Show Carton Materials'} <br />
                <small>{t('quote_builder_widget.show_carton_materials_info') || 'When enabled, customers can select carton material options'}</small>
              </td>
              <td>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={showCartonMaterials}
                    onChange={(e) => setShowCartonMaterials(e.target.checked)}
                    style={{ marginRight: '8px', width: '18px', height: '18px' }}
                  />
                  {showCartonMaterials
                    ? (t('quote_builder_widget.enabled') || 'Enabled')
                    : (t('quote_builder_widget.disabled') || 'Disabled')}
                </label>
              </td>
            </tr>

            {showCartonMaterials && (
              <tr>
                <td colSpan="2">
                  <div style={{ marginBottom: '10px' }}>
                    <strong>{t('quote_builder_widget.carton_materials') || 'Carton Materials'}</strong>
                    <br />
                    <small style={{ color: '#666' }}>{t('quote_builder_widget.carton_materials_info') || 'Materials available in the widget for carton drawings'}</small>
                  </div>
                  <CartonMaterialsSection onMaterialsChange={() => setPreviewKey(Date.now())} />
                </td>
              </tr>
            )}

            <tr>
              <td>
                {t('quote_builder_widget.show_corrugated_cardboard_materials') || 'Show Corrugated Cardboard Materials'} <br />
                <small>{t('quote_builder_widget.show_corrugated_cardboard_materials_info') || 'When enabled, customers can select corrugated cardboard material options'}</small>
              </td>
              <td>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={showCorrugatedCardboardMaterials}
                    onChange={(e) => setShowCorrugatedCardboardMaterials(e.target.checked)}
                    style={{ marginRight: '8px', width: '18px', height: '18px' }}
                  />
                  {showCorrugatedCardboardMaterials
                    ? (t('quote_builder_widget.enabled') || 'Enabled')
                    : (t('quote_builder_widget.disabled') || 'Disabled')}
                </label>
              </td>
            </tr>

            {showCorrugatedCardboardMaterials && (
              <tr>
                <td colSpan="2">
                  <div style={{ marginBottom: '10px' }}>
                    <strong>{t('quote_builder_widget.corrugated_cardboard_materials') || 'Corrugated Cardboard Materials'}</strong>
                    <br />
                    <small style={{ color: '#666' }}>{t('quote_builder_widget.corrugated_cardboard_materials_info') || 'Materials available in the widget for corrugated cardboard drawings'}</small>
                  </div>
                  <CorrugatedCardboardMaterialsSection onMaterialsChange={() => setPreviewKey(Date.now())} />
                </td>
              </tr>
            )}

            <tr>
              <td colSpan="2">
                {errorMessage != '' && <div className="alert alert-danger fade in"> <strong>  <i className="fa fa-exclamation-circle"></i> {errorMessage} </strong> </div>}
                {successMessage != '' && <div className="alert alert-success fade in"> <strong>  <i className="fa fa-check-circle"></i> {successMessage} </strong> </div>}
                <button className="btn simple-btn" type="button" onClick={handleSave}> {t('quote_builder_widget.save')} </button> &nbsp; &nbsp;
                <button className="btn" onClick={handleCancel}> {t('quote_builder_widget.cancel')} </button>
              </td>
            </tr>

          </tbody>
        </table>
      </div>

      {/* Preview Column - Sticky */}
      {savedIdentifier && (
        <div style={{ flex: '1 1 400px', minWidth: '300px', position: 'sticky', top: '20px', alignSelf: 'flex-start' }}>
          <h4>{t('quote_builder_widget.preview')}</h4>
          <hr />
          <div style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '10px', backgroundColor: '#fafafa' }}>
            <WidgetPreview
              identifier={savedIdentifier}
              templateIdentifier={SAMPLE_TEMPLATE_IDENTIFIER}
              previewKey={previewKey}
            />
          </div>
        </div>
      )}
    </div>

    {supportsCustomCss && (
      <CssEditorModal
        isOpen={isCssEditorOpen}
        onClose={() => setIsCssEditorOpen(false)}
        onSave={(newCss) => {
          setCustomCss(newCss);
          setIsCssEditorOpen(false);
          saveWidget({ customCss: newCss });
        }}
        currentCss={customCss}
        theme={selectedTheme}
        domains={domains}
      />
    )}

    </div>
  )
}

export default FormNew;
